<template>
  <div class="wechat-login-modal" v-if="visible">
    <div class="modal-mask" @click="closeModal"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h3>微信扫码登录</h3>
        <button class="close-btn" @click="closeModal">×</button>
      </div>
      
      <div class="modal-body">
        <!-- 二维码显示 -->
        <div v-if="!showBindForm" class="qrcode-section">
          <div class="qrcode-container" v-loading="qrLoading">
            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="微信扫码登录" class="qrcode-image">
            <div v-else class="qrcode-placeholder">
              <i class="el-icon-picture"></i>
              <p>加载二维码中...</p>
            </div>
          </div>
          
          <div class="qrcode-tips">
            <p>请使用微信扫一扫</p>
            <p>扫描二维码登录</p>
          </div>
          
          <div class="status-info" v-if="statusMessage">
            <i :class="statusIcon"></i>
            <span>{{ statusMessage }}</span>
          </div>
        </div>
        
        <!-- 绑定手机号表单 -->
        <div v-if="showBindForm" class="bind-form">
          <div class="bind-tips">
            <i class="el-icon-info"></i>
            <span>检测到该微信未绑定账号，请绑定手机号</span>
          </div>
          
          <div class="form-group">
            <label>手机号</label>
            <input 
              v-model="bindForm.phone" 
              type="tel" 
              placeholder="请输入手机号"
              maxlength="11"
            >
          </div>
          
          <div class="form-group">
            <label>验证码</label>
            <div class="sms-code-input">
              <input 
                v-model="bindForm.smsCode" 
                type="text" 
                placeholder="请输入验证码"
                maxlength="6"
              >
              <button 
                class="send-sms-btn" 
                :disabled="smsCountdown > 0"
                @click="sendSmsCode"
              >
                {{ smsCountdown > 0 ? `${smsCountdown}s` : '发送验证码' }}
              </button>
            </div>
          </div>
          
          <button 
            class="bind-btn" 
            :disabled="!canBind"
            @click="handleBind"
          >
            绑定并登录
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getWechatQrCode, checkWechatStatus } from '@/api/auth/login'

export default {
  name: 'WechatLogin',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      qrCodeUrl: '',
      tempCode: '',
      qrLoading: false,
      checkTimer: null,
      statusMessage: '',
      statusIcon: 'el-icon-time',
      showBindForm: false,
      bindForm: {
        phone: '',
        smsCode: ''
      },
      smsCountdown: 0
    }
  },
  computed: {
    canBind() {
      return this.bindForm.phone && this.bindForm.phone.length === 11 && 
             this.bindForm.smsCode && this.bindForm.smsCode.length === 6
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initWechatLogin()
      } else {
        this.cleanup()
      }
    }
  },
  methods: {
    async initWechatLogin() {
      try {
        this.qrLoading = true
        this.statusMessage = '正在生成二维码...'
        
        const response = await getWechatQrCode()
        if (response.data.code === 200) {
          this.qrCodeUrl = response.data.data.qrCodeUrl
          this.tempCode = response.data.data.tempCode
          this.startStatusCheck()
        }
      } catch (error) {
        this.statusMessage = '二维码生成失败'
        console.error('生成二维码失败:', error)
      } finally {
        this.qrLoading = false
      }
    },
    
    startStatusCheck() {
      this.statusMessage = '请使用微信扫描二维码'
      this.statusIcon = 'el-icon-time'
      
      this.checkTimer = setInterval(async () => {
        try {
          const response = await checkWechatStatus(this.tempCode)
          const status = response.data.data.status
          
          if (status === 'SCANNED') {
            this.statusMessage = '已扫描，请在微信端确认'
            this.statusIcon = 'el-icon-success'
          } else if (status === 'CONFIRMED') {
            this.statusMessage = '登录成功'
            this.statusIcon = 'el-icon-success'
            this.handleWechatLogin()
          }
        } catch (error) {
          console.error('检查状态失败:', error)
        }
      }, 2000)
    },
    
    async handleWechatLogin() {
      try {
        const result = await this.$store.dispatch('auth/wechatLogin', {
          tempCode: this.tempCode
        })
        
        if (result && result.success) {
          this.$message.success('微信登录成功')
          this.$emit('login-success', result)
          this.closeModal()
        } else {
          // 如果需要绑定手机号
          if (result.message && result.message.includes('未绑定')) {
            this.showBindForm = true
            this.statusMessage = '请绑定手机号'
          } else {
            this.$message.error(result.message || '登录失败')
          }
        }
      } catch (error) {
        console.error('微信登录失败:', error)
        this.$message.error('登录失败')
      }
    },
    
    async sendSmsCode() {
      if (!this.bindForm.phone || this.bindForm.phone.length !== 11) {
        this.$message.warning('请输入正确的手机号')
        return
      }
      
      try {
        // 调用发送短信验证码的API
        // await sendSmsCode({ phone: this.bindForm.phone })
        this.$message.success('验证码已发送')
        this.startSmsCountdown()
      } catch (error) {
        this.$message.error('发送验证码失败')
      }
    },
    
    startSmsCountdown() {
      this.smsCountdown = 60
      const timer = setInterval(() => {
        this.smsCountdown--
        if (this.smsCountdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    
    async handleBind() {
      try {
        const result = await this.$store.dispatch('auth/wechatLogin', {
          tempCode: this.tempCode,
          phone: this.bindForm.phone,
          smsCode: this.bindForm.smsCode
        })
        
        if (result && result.success) {
          this.$message.success('绑定并登录成功')
          this.$emit('login-success', result)
          this.closeModal()
        } else {
          this.$message.error(result.message || '绑定失败')
        }
      } catch (error) {
        console.error('绑定失败:', error)
        this.$message.error('绑定失败')
      }
    },
    
    closeModal() {
      this.cleanup()
      this.$emit('update:visible', false)
    },
    
    cleanup() {
      if (this.checkTimer) {
        clearInterval(this.checkTimer)
        this.checkTimer = null
      }
      this.showBindForm = false
      this.bindForm.phone = ''
      this.bindForm.smsCode = ''
      this.smsCountdown = 0
    }
  },
  
  beforeDestroy() {
    this.cleanup()
  }
}
</script>

<style scoped lang="scss">
.wechat-login-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .modal-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
  }
  
  .modal-content {
    position: relative;
    width: 360px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #f0f0f0;
    
    h3 {
      margin: 0;
      font-size: 1.2rem;
      color: #333;
    }
    
    .close-btn {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: #999;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      
      &:hover {
        color: #666;
      }
    }
  }
  
  .modal-body {
    padding: 24px;
  }
  
  .qrcode-container {
    width: 200px;
    height: 200px;
    margin: 0 auto 20px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    
    .qrcode-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    
    .qrcode-placeholder {
      text-align: center;
      color: #999;
      
      i {
        font-size: 3rem;
        margin-bottom: 8px;
      }
    }
  }
  
  .qrcode-tips {
    text-align: center;
    margin-bottom: 16px;
    
    p {
      margin: 4px 0;
      color: #666;
      font-size: 0.9rem;
    }
  }
  
  .status-info {
    text-align: center;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    color: #666;
    
    i {
      margin-right: 6px;
      color: #1890ff;
    }
  }
  
  .bind-form {
    .bind-tips {
      display: flex;
      align-items: center;
      padding: 12px;
      background: #e6f7ff;
      border: 1px solid #91d5ff;
      border-radius: 6px;
      margin-bottom: 20px;
      color: #1890ff;
      
      i {
        margin-right: 8px;
      }
    }
    
    .form-group {
      margin-bottom: 16px;
      
      label {
        display: block;
        margin-bottom: 6px;
        font-weight: 500;
        color: #333;
      }
      
      input {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        font-size: 0.9rem;
        
        &:focus {
          border-color: #409eff;
          outline: none;
        }
      }
      
      .sms-code-input {
        display: flex;
        gap: 8px;
        
        input {
          flex: 1;
        }
        
        .send-sms-btn {
          padding: 10px 16px;
          background: #409eff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          white-space: nowrap;
          
          &:disabled {
            background: #c0c4cc;
            cursor: not-allowed;
          }
        }
      }
    }
    
    .bind-btn {
      width: 100%;
      padding: 12px;
      background: #67c23a;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      cursor: pointer;
      
      &:disabled {
        background: #c0c4cc;
        cursor: not-allowed;
      }
      
      &:hover:not(:disabled) {
        background: #5daf34;
      }
    }
  }
}
</style>